import type { AdDate, LodgingBookingStatus, LodgingExtraUnit, LodgingStay, MarkLodging } from '../../api';
import { kronor } from './adPurchase';

// Boende (L1): texter och datum för bokningsvyn, inställningarna och "Mina köp".
// Priset räknas av API:t (lodgingQuote); här formateras bara det som visas.

const DAY = 86_400_000;
export const addDays = (d: string, n: number) => new Date(Date.parse(`${d}T00:00:00Z`) + n * DAY).toISOString().slice(0, 10);
/** Dagens datum i svensk tid (YYYY-MM-DD). */
export const todayIso = (now = new Date()) => now.toLocaleDateString('sv-SE', { timeZone: 'Europe/Stockholm' });
export const nightsBetween = (checkIn: string, checkOut: string) =>
  checkIn && checkOut && checkOut > checkIn ? Math.round((Date.parse(`${checkOut}T00:00:00Z`) - Date.parse(`${checkIn}T00:00:00Z`)) / DAY) : 0;

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;
export const nightsText = (n: number) => plural(n, 'natt', 'nätter');
export const guestsText = (n: number) => plural(n, 'person', 'personer');

/** "800 kr / natt" eller "350 kr / person och natt". */
export function lodgingPriceText(l: Pick<MarkLodging, 'pricePerNight' | 'priceUnit'>): string {
  return `${kronor(l.pricePerNight)} / ${l.priceUnit === 'person' ? 'person och natt' : 'natt'}`;
}

/** "Ingår", "150 kr / person", "600 kr / bokning" eller null om tillvalet inte erbjuds. */
export function extraText(included: boolean, price: number | null, unit: LodgingExtraUnit): string | null {
  if (included) return 'Ingår';
  if (price == null || price <= 0) return null;
  return `${kronor(price)} / ${unit === 'person' ? 'person' : 'bokning'}`;
}

/** "6 bäddar · 3 rum · hela boendet". */
export function capacityText(l: Pick<MarkLodging, 'capacity' | 'rooms' | 'priceUnit'>): string {
  return [plural(l.capacity, 'bädd', 'bäddar'), l.rooms ? plural(l.rooms, 'rum', 'rum') : null, l.priceUnit === 'lodging' ? 'hyrs som helhet' : 'bäddar hyrs var för sig']
    .filter(Boolean).join(' · ');
}

/** Förslag på vistelse till en enskild jakt: natten före första jaktdagen till dagen efter sista. */
export function stayForDates(date: AdDate | null | undefined, today = todayIso()): { checkIn: string; checkOut: string } {
  if (!date) return { checkIn: addDays(today, 1), checkOut: addDays(today, 2) };
  const checkIn = addDays(date.start, -1) >= today ? addDays(date.start, -1) : date.start;
  return { checkIn, checkOut: addDays(date.end, 1) };
}

export function emptyStay(l: Pick<MarkLodging, 'id'>, dates?: { checkIn: string; checkOut: string }): LodgingStay {
  const d = dates ?? stayForDates(null);
  return { lodgingId: l.id, checkIn: d.checkIn, checkOut: d.checkOut, guests: 1, linens: false, cleaning: false };
}

/** Varför boendet inte kan bokas med kort. */
export function lodgingBlockedText(reason: string | null | undefined): string {
  switch (reason) {
    case 'owner_not_connected': return 'Markägaren har inte aktiverat kortbetalning ännu.';
    case 'not_active': return 'Boendet går inte att boka just nu.';
    default: return 'Går inte att boka just nu.';
  }
}

export function lodgingStatusText(status: LodgingBookingStatus): { label: string; tone: 'ok' | 'wait' | 'off' } {
  switch (status) {
    case 'paid': return { label: 'Bokat och betalt', tone: 'ok' };
    case 'reserved': return { label: 'Reserverat – dras när jakten godkänns', tone: 'wait' };
    case 'checkout': return { label: 'Betalning pågår', tone: 'wait' };
    case 'rejected': return { label: 'Släppt – jakten godkändes inte', tone: 'off' };
    case 'released': return { label: 'Släppt – ingen beslutade i tid', tone: 'off' };
    case 'cancelled': return { label: 'Avbrutet', tone: 'off' };
  }
}

export const DEFAULT_CANCELLATION = 'Avbokning sker direkt med markägaren. Qvenari kan inte avboka eller återbetala boende ännu.';

/** "fre 6 nov" */
export const shortDay = (d: string) => new Date(`${d}T12:00:00Z`).toLocaleDateString('sv-SE', { timeZone: 'UTC', weekday: 'short', day: 'numeric', month: 'short' }).replace(/\./g, '');
export const stayText = (checkIn: string, checkOut: string) => `${shortDay(checkIn)} – ${shortDay(checkOut)} · ${nightsText(nightsBetween(checkIn, checkOut))}`;
